/**
 * Volcengine AST 2.0 Setup Tutorial Page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useI18n, Locale } from '@/lib/i18n';
import { localizePath } from '@/lib/locale-path';
import { useDocumentMeta } from '@/lib/document-meta';
import { Lightbox } from '@/components/docs/Lightbox';
import './tutorials.scss';

interface Translation {
  pageTitle: string;
  backLink: string;
  overview: { title: string; content: string };
  steps: { title: string; content: string; screenshot?: boolean }[];
  features: {
    title: string;
    sections: {
      title: string;
      content?: string;
      items: string[];
    }[];
  };
  troubleshooting: {
    title: string;
    sections: {
      title: string;
      items?: { label: string; content: string }[];
      steps?: string[];
    }[];
  };
  helpText: string;
  externalLinkText: string;
}

const ACCENT_COLOR = '#ff6a00';

const translations: Record<Locale, Translation> = {
  zh: {
    pageTitle: '豆包同声传译 2.0 设置教程',
    backLink: '返回 AI 提供商',
    overview: {
      title: '概述',
      content: '本教程将指导您设置豆包同声传译 2.0 以与 Sokuji 配合使用。豆包同声传译 2.0 是语音到语音同声传译服务，可以复刻说话者的声音，也可以从音色库中挑选固定音色。',
    },

    steps: [
      { title: '注册火山引擎账户', content: '访问 <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> 并注册账户。如果您已有字节跳动/火山引擎账户，可以直接登录。' },
      { title: '进入语音技术控制台', content: '登录后，在左侧菜单中找到 <strong>"豆包同声传译2.0大模型"</strong>，或直接访问 <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">语音技术控制台</a>。' },
      { title: '创建应用', content: '在语音技术控制台中，点击 <strong>"创建应用"</strong> 按钮来创建一个新的应用。为应用命名（例如"Sokuji Translation"），然后确认创建。' },
      { title: '获取 APP ID 和 Access Token', content: '点击左侧菜单的 <strong>"豆包同声传译2.0大模型"</strong>，在页面顶部的应用名称下拉框中选择刚创建的应用，然后复制下方显示的 <strong>APP ID</strong> 和 <strong>Access Token</strong>。Sokuji 也接受火山引擎的 API Key，两种凭证二选一即可。', screenshot: true },
      { title: '在 Sokuji 中配置', content: '打开 Sokuji，在设置面板中选择 <strong>"豆包同声传译 2.0"</strong> 作为 AI 提供商。凭证方式选择<strong>应用 ID + 访问令牌</strong>并填入 <strong>APP ID</strong> 和 <strong>Access Token</strong>，或选择 <strong>API 密钥</strong>并填入 API Key。然后选择源语言和目标语言；语音模式（自动、按住说话、按住翻译）是对所有提供商生效的全局设置。' },
      { title: '测试连接', content: '点击开始会话按钮，对着麦克风说话，验证语音到语音的同声传译是否正常工作。您应能看到译文。朗读的翻译会送入虚拟麦克风给对方听；如果您自己也想听到，请在<strong>语音</strong>区块（快捷设置，或设置 → 高级 → 音频）中打开<strong>我也听</strong>。' },
    ],
    features: {
      title: '功能介绍',
      sections: [
        {
          title: '什么是豆包同声传译 2.0？',
          content: '豆包同声传译 2.0 是端到端语音翻译服务：',
          items: [
            '语音到语音的同声传译，无需中间文本步骤',
            '声音复刻：翻译后的语音保留说话者的音色',
            '固定音色：从豆包的音色库中挑选，按目标语言各选一个，可按性别、年龄和风格筛选并试听',
            '我听到的声音：在语音区块中打开“我听到的翻译”后，对方的翻译也会朗读给您听，语音设置中会出现两行：“对方听到的声音”和“我听到的声音”。“我听到的声音”列出的是您自己语言的音色；在这一行选择“复刻说话人的声音”，复刻的是对方的声音。音色按语言保存，为某种语言选的音色，在两行中朗读这种语言时都会使用。选择“中文 + 英语”时，一行同时用于双方',
            '低延迟的实时翻译处理',
            '中文与英语可在一个会话里双向互译',
          ],
        },
        {
          title: '支持的语言',
          content: '每个语言对中必须有一方是中文或英语：',
          items: [
            '可朗读（语音到语音）：中文、英语、日语、印尼语、西班牙语、葡萄牙语、德语、法语；从中文或英语出发还可以朗读成韩语',
            '只能转成文字：韩语、土耳其语、马来语、荷兰语、罗马尼亚语、波兰语、捷克语、阿拉伯语、泰语、越南语、俄语、意大利语',
            '方言：粤语、吴语（上海话）只能作为源语言',
            '“中文 + 英语”：一个会话里两种语言互译',
          ],
        },
        {
          title: '语音模式',
          items: [
            '自动：由服务端检测说话的开始和结束，适合自然对话场景',
            '按住说话：按住空格键或麦克风按钮时才发送语音，适合嘈杂环境或需要精确控制的场景',
            '按住翻译：与按住说话相同，只是松开时你的原声会直接进入虚拟麦克风',
          ],
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      sections: [
        {
          title: '常见问题',
          items: [
            { label: '认证错误', content: '请验证 APP ID 和 Access Token（或 API Key）是否正确复制，确保没有多余的空格，并确认 Sokuji 里的凭证方式与您填入的凭证一致。' },
            { label: '连接错误', content: '检查网络连接是否正常，确保防火墙没有阻止 WebSocket 连接。如果使用代理，请确保代理支持 WebSocket。' },
            { label: '不支持的语言对', content: '请确认您选择的源语言和目标语言组合在豆包同声传译 2.0 支持的范围内。' },
          ],
        },
        {
          title: '使用建议',
          steps: [
            '使用稳定的网络连接以确保最佳翻译质量',
            '在安静的环境中使用以获得最佳语音识别效果',
            '如果翻译质量不理想，尝试切换语音模式',
            '定期检查火山引擎控制台中的使用量和配额',
          ],
        },
      ],
    },
    helpText: '需要更多帮助？请访问火山引擎语音技术控制台查看官方文档，或访问我们的 GitHub 仓库获取支持。',
    externalLinkText: '火山引擎语音技术控制台',
  },
  en: {
    pageTitle: 'Doubao AST 2.0 Setup Tutorial',
    backLink: 'Back to AI Providers',
    overview: {
      title: 'Overview',
      content: 'This tutorial will guide you through setting up Doubao AST 2.0 for use with Sokuji. Doubao AST 2.0 is a speech-to-speech simultaneous interpretation service that can clone the speaker’s voice or speak in a fixed voice picked from its library.',
    },

    steps: [
      { title: 'Register a Volcengine Account', content: 'Go to <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> and sign up for an account. If you already have a ByteDance/Volcengine account, you can sign in directly.' },
      { title: 'Navigate to the Speech Console', content: 'After logging in, select <strong>"Doubao Simultaneous Interpreting 2.0"</strong> from the left menu, or visit the <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">Speech Console</a> directly.' },
      { title: 'Create an Application', content: 'In the Speech console, click <strong>"Create Application"</strong> to create a new application. Name it (e.g., "Sokuji Translation") and confirm creation.' },
      { title: 'Get APP ID and Access Token', content: 'Click <strong>"Doubao Simultaneous Interpreting 2.0"</strong> in the left menu. Select your newly created application from the application name dropdown at the top of the page, then copy the <strong>APP ID</strong> and <strong>Access Token</strong> shown below. Sokuji also accepts a Volcengine API Key; either kind of credential will do.', screenshot: true },
      { title: 'Configure in Sokuji', content: 'Open Sokuji and select <strong>"Doubao AST 2.0"</strong> as your AI provider in the settings panel. Choose the credential kind <strong>App ID + Access Token</strong> and enter your <strong>APP ID</strong> and <strong>Access Token</strong>, or choose <strong>API key</strong> and paste the API Key. Then choose the source and target languages; Speech Mode (Auto, Push-to-Talk or Push-to-Translate) is one setting for every provider.' },
      { title: 'Test the Connection', content: 'Click the Start Session button and speak into your microphone to verify that speech-to-speech translation is working. You should see the translation. The spoken translation goes into the virtual microphone for the other side; to hear it yourself, turn on <strong>I hear it too</strong> in the <strong>Speech</strong> block (Quick settings, or Settings → Advanced → Audio).' },
    ],
    features: {
      title: 'Features',
      sections: [
        {
          title: 'What is Doubao AST 2.0?',
          content: 'Doubao AST 2.0 is an end-to-end speech translation service:',
          items: [
            'Speech-to-speech simultaneous translation without intermediate text steps',
            'Voice cloning: translated speech preserves the speaker\'s voice characteristics',
            'Fixed voices: pick one per target language from Doubao\'s library, filtered by gender, age and style, with a preview',
            'Voice I hear: with Translation I hear on in the Speech block, the other side\'s translation is read aloud to you too, and Voice Settings shows two rows, Voice the other side hears and Voice I hear. Voice I hear lists the voices of your own language; Clone the speaker\'s voice there clones the other person. A voice is kept per language, so a voice you pick for a language speaks it in either row. With Chinese + English, one row serves both sides',
            'Low-latency real-time translation processing',
            'Chinese and English can be translated both ways in one session',
          ],
        },
        {
          title: 'Supported Languages',
          content: 'One side of every language pair must be Chinese or English:',
          items: [
            'Spoken (speech-to-speech): Chinese, English, Japanese, Indonesian, Spanish, Portuguese, German, French; from Chinese or English, Korean can be spoken too',
            'Text only: Korean, Turkish, Malay, Dutch, Romanian, Polish, Czech, Arabic, Thai, Vietnamese, Russian, Italian',
            'Dialects: Cantonese and Wu (Shanghainese), as the source language only',
            '“Chinese + English”: both languages translated each way in one session',
          ],
        },
        {
          title: 'Speech Mode',
          items: [
            'Auto: the service detects when speech starts and ends, suitable for natural conversations',
            'Push-to-Talk: audio is sent only while you hold Space or the microphone button, suitable for noisy environments or when precise control is needed',
            'Push-to-Translate: the same as Push-to-Talk, except that while you are not holding, your own voice goes straight into the virtual microphone',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      sections: [
        {
          title: 'Common Issues',
          items: [
            { label: 'Authentication Error', content: 'Verify that your APP ID and Access Token (or API Key) are correctly copied without extra spaces, and that the credential kind selected in Sokuji matches what you entered.' },
            { label: 'Connection Error', content: 'Check your network connection and ensure firewalls are not blocking WebSocket connections. If using a proxy, make sure it supports WebSocket.' },
            { label: 'Unsupported Language Pair', content: 'Confirm that your selected source and target language combination is within the supported range of Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Tips for Best Results',
          steps: [
            'Use a stable network connection for best translation quality',
            'Use in a quiet environment for the best speech recognition',
            'If translation quality is not ideal, try switching the Speech Mode',
            'Regularly check usage and quota in the Volcengine console',
          ],
        },
      ],
    },
    helpText: 'Need more help? Visit the Volcengine Speech console for official documentation or check our GitHub repository for support.',
    externalLinkText: 'Volcengine Speech Console',
  },
  ja: {
    pageTitle: 'Doubao AST 2.0 セットアップチュートリアル',
    backLink: 'AI プロバイダーに戻る',
    overview: {
      title: '概要',
      content: 'このチュートリアルでは、Sokuji で使用するための Doubao AST 2.0 のセットアップを説明します。Doubao AST 2.0 は音声から音声への同時通訳サービスで、話者の声を複製することも、ライブラリから選んだ固定の声で話すこともできます。',
    },

    steps: [
      { title: '火山エンジンアカウントを登録', content: '<a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> にアクセスしてアカウントを登録します。ByteDance/火山エンジンのアカウントをお持ちの場合は、直接サインインできます。' },
      { title: '音声技術コンソールに移動', content: 'ログイン後、左側メニューから <strong>「豆包同時通訳2.0大規模モデル」</strong> を選択するか、<a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">音声技術コンソール</a>に直接アクセスします。' },
      { title: 'アプリケーションを作成', content: '音声技術コンソールで、<strong>「アプリケーション作成」</strong>をクリックして新しいアプリケーションを作成します。名前を付けて（例：「Sokuji Translation」）、作成を確認します。' },
      { title: 'APP ID と Access Token を取得', content: '左側メニューの <strong>「豆包同時通訳2.0大規模モデル」</strong> をクリックします。ページ上部のアプリケーション名ドロップダウンから作成したアプリケーションを選択し、下に表示される <strong>APP ID</strong> と <strong>Access Token</strong> をコピーします。Sokuji は火山エンジンの API Key も受け付けます。どちらか一方の認証情報があれば十分です。', screenshot: true },
      { title: 'Sokuji で設定', content: 'Sokuji を開き、設定パネルで <strong>「Doubao AST 2.0」</strong> を AI プロバイダーとして選択します。認証方式で<strong>アプリ ID + アクセストークン</strong>を選んで <strong>APP ID</strong> と <strong>Access Token</strong> を入力するか、<strong>API キー</strong>を選んで API Key を貼り付けます。次にソース言語とターゲット言語を選択します。音声モード（自動、プッシュトゥトーク、プッシュトゥトランスレート）はすべてのプロバイダーに共通の 1 つの設定です。' },
      { title: '接続をテスト', content: 'セッション開始ボタンをクリックし、マイクに向かって話して、音声から音声への翻訳が動作していることを確認します。翻訳が表示されるはずです。読み上げられた翻訳は相手向けに仮想マイクへ送られます。自分でも聞くには、<strong>音声</strong>ブロック（クイック設定、または 設定 → 詳細 → オーディオ）で<strong>自分にも再生</strong>をオンにします。' },
    ],
    features: {
      title: '機能紹介',
      sections: [
        {
          title: 'Doubao AST 2.0 とは？',
          content: 'Doubao AST 2.0 はエンドツーエンド音声翻訳サービスです：',
          items: [
            '中間テキストステップなしの音声から音声への同時通訳',
            '声の複製：翻訳された音声が話者の声質を保持',
            '固定の声：ターゲット言語ごとに Doubao のライブラリから 1 つ選べます。性別、年齢、スタイルで絞り込み、試聴もできます',
            '自分に聞こえる音声：音声ブロックで「自分に聞こえる翻訳」をオンにすると、相手の翻訳もあなたに読み上げられ、音声設定に「相手に聞こえる音声」と「自分に聞こえる音声」の 2 行が表示されます。「自分に聞こえる音声」にはあなた自身の言語の声が並び、そこで「話者の声を複製」を選ぶと相手の声が複製されます。声は言語ごとに保存されるため、ある言語に選んだ声はどちらの行でもその言語の読み上げに使われます。「中国語 + 英語」では 1 行が双方に使われます',
            '低遅延のリアルタイム翻訳処理',
            '中国語と英語は 1 つのセッションで双方向に翻訳可能',
          ],
        },
        {
          title: 'サポートされている言語',
          content: 'どの言語ペアも、一方は中国語または英語である必要があります：',
          items: [
            '音声出力あり（音声から音声）：中国語、英語、日本語、インドネシア語、スペイン語、ポルトガル語、ドイツ語、フランス語。中国語または英語からは韓国語も音声で出力できます',
            'テキストのみ：韓国語、トルコ語、マレー語、オランダ語、ルーマニア語、ポーランド語、チェコ語、アラビア語、タイ語、ベトナム語、ロシア語、イタリア語',
            '方言：広東語と呉語（上海語）はソース言語としてのみ',
            '「中国語 + 英語」：1 つのセッションで両方の言語を双方向に翻訳',
          ],
        },
        {
          title: '音声モード',
          items: [
            '自動：サービスが発話の開始と終了を検出します。自然な会話に適しています',
            'プッシュトゥトーク：スペースキーまたはマイクボタンを押している間だけ音声が送られます。騒がしい環境や正確な制御が必要な場合に適しています',
            'プッシュトゥトランスレート：プッシュトゥトークと同じですが、押していない間はあなたの声がそのまま仮想マイクに入ります',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      sections: [
        {
          title: '一般的な問題',
          items: [
            { label: '認証エラー', content: 'APP ID と Access Token（または API Key）が余分なスペースなしで正しくコピーされているか、また Sokuji で選んだ認証方式が入力した認証情報と合っているか確認してください。' },
            { label: '接続エラー', content: 'ネットワーク接続を確認し、ファイアウォールが WebSocket 接続をブロックしていないことを確認してください。プロキシを使用している場合は、WebSocket がサポートされていることを確認してください。' },
            { label: 'サポートされていない言語ペア', content: '選択したソース言語とターゲット言語の組み合わせが Doubao AST 2.0 のサポート範囲内であることを確認してください。' },
          ],
        },
        {
          title: '最良の結果を得るために',
          steps: [
            '最高の翻訳品質のために安定したネットワーク接続を使用する',
            '最適な音声認識のために静かな環境で使用する',
            '翻訳品質が理想的でない場合は、音声モードの切り替えを試す',
            '火山エンジンコンソールで使用量とクォータを定期的に確認する',
          ],
        },
      ],
    },
    helpText: 'さらにヘルプが必要ですか？公式ドキュメントは火山エンジン音声技術コンソールをご覧いただくか、GitHub リポジトリでサポートを受けてください。',
    externalLinkText: '火山エンジン音声技術コンソール',
  },
  ko: {
    pageTitle: 'Doubao AST 2.0 설정 튜토리얼',
    backLink: 'AI 제공업체로 돌아가기',
    overview: {
      title: '개요',
      content: '이 튜토리얼은 Sokuji와 함께 사용하기 위한 Doubao AST 2.0 설정을 안내합니다. Doubao AST 2.0은 화자의 목소리를 복제하거나 라이브러리에서 고른 고정 음성으로 말할 수 있는 음성 대 음성 동시 통역 서비스입니다.',
    },

    steps: [
      { title: 'Volcengine 계정 등록', content: '<a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a>에 접속하여 계정을 등록하세요. ByteDance/Volcengine 계정이 있다면 직접 로그인할 수 있습니다.' },
      { title: '음성 기술 콘솔로 이동', content: '로그인 후 왼쪽 메뉴에서 <strong>"豆包 동시통역 2.0 대규모 모델"</strong>을 선택하거나, <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">음성 기술 콘솔</a>에 직접 접속하세요.' },
      { title: '애플리케이션 생성', content: '음성 기술 콘솔에서 <strong>"애플리케이션 생성"</strong>을 클릭하여 새 애플리케이션을 만드세요. 이름을 지정하고 (예: "Sokuji Translation") 생성을 확인합니다.' },
      { title: 'APP ID 및 Access Token 획득', content: '왼쪽 메뉴에서 <strong>"豆包 동시통역 2.0 대규모 모델"</strong>을 클릭합니다. 페이지 상단의 애플리케이션 이름 드롭다운에서 방금 생성한 애플리케이션을 선택한 후, 아래에 표시되는 <strong>APP ID</strong>와 <strong>Access Token</strong>을 복사하세요. Sokuji는 Volcengine API Key도 받으므로, 두 종류의 자격 증명 중 어느 것이든 사용할 수 있습니다.', screenshot: true },
      { title: 'Sokuji에서 설정', content: 'Sokuji를 열고 설정 패널에서 <strong>"Doubao AST 2.0"</strong>을 AI 제공업체로 선택하세요. 자격 증명 종류로 <strong>App ID + Access Token</strong>을 선택하고 <strong>APP ID</strong>와 <strong>Access Token</strong>을 입력하거나, <strong>API 키</strong>를 선택하고 API Key를 붙여넣으세요. 그런 다음 소스 및 대상 언어를 선택하세요. 음성 모드(자동, 푸시 투 토크, 푸시 투 트랜슬레이트)는 모든 제공업체에 공통으로 적용되는 하나의 설정입니다.' },
      { title: '연결 테스트', content: '세션 시작 버튼을 클릭하고 마이크에 대고 말하여 음성 대 음성 번역이 작동하는지 확인하세요. 번역이 표시되어야 합니다. 읽어 주는 번역은 상대방을 위해 가상 마이크로 보내집니다. 직접 들으려면 <strong>음성</strong> 블록(빠른 설정, 또는 설정 → 고급 → 오디오)에서 <strong>나도 듣기</strong>를 켜세요.' },
    ],
    features: {
      title: '기능 소개',
      sections: [
        {
          title: 'Doubao AST 2.0이란?',
          content: 'Doubao AST 2.0은 엔드투엔드 음성 번역 서비스입니다:',
          items: [
            '중간 텍스트 단계 없이 음성 대 음성 동시 통역',
            '음성 복제: 번역된 음성이 화자의 음색을 유지',
            '고정 음성: 대상 언어별로 Doubao 라이브러리에서 하나씩 선택, 성별·연령·스타일로 필터링하고 미리 들을 수 있음',
            '내가 듣는 음성: 음성 블록에서 "내가 듣는 번역"을 켜면 상대방의 번역도 나에게 읽어 주며, 음성 설정에 "상대방에게 들리는 음성"과 "내가 듣는 음성" 두 행이 나타납니다. "내가 듣는 음성"에는 내 언어의 음성이 표시되고, 여기서 "화자 목소리 복제"를 고르면 상대방의 목소리를 복제합니다. 음성은 언어별로 저장되므로 어떤 언어에 고른 음성은 어느 행에서든 그 언어를 읽을 때 쓰입니다. "중국어 + 영어"에서는 한 행이 양쪽에 쓰입니다',
            '낮은 지연 시간의 실시간 번역 처리',
            '중국어와 영어는 한 세션에서 양방향으로 번역 가능',
          ],
        },
        {
          title: '지원 언어',
          content: '모든 언어 쌍에서 한쪽은 중국어 또는 영어여야 합니다:',
          items: [
            '음성 출력(음성 대 음성): 중국어, 영어, 일본어, 인도네시아어, 스페인어, 포르투갈어, 독일어, 프랑스어; 중국어 또는 영어에서 출발하면 한국어도 음성으로 출력 가능',
            '텍스트만: 한국어, 터키어, 말레이어, 네덜란드어, 루마니아어, 폴란드어, 체코어, 아랍어, 태국어, 베트남어, 러시아어, 이탈리아어',
            '방언: 광둥어와 우어(상하이어), 소스 언어로만 사용 가능',
            '“중국어 + 영어”: 한 세션에서 두 언어를 서로 번역',
          ],
        },
        {
          title: '음성 모드',
          items: [
            '자동: 서비스가 발화의 시작과 끝을 감지, 자연스러운 대화에 적합',
            '푸시 투 토크: 스페이스 키나 마이크 버튼을 누르고 있는 동안에만 음성이 전송, 시끄러운 환경이나 정밀한 제어가 필요할 때 적합',
            '푸시 투 트랜슬레이트: 푸시 투 토크와 같지만, 누르고 있지 않은 동안에는 내 목소리가 그대로 가상 마이크로 들어감',
          ],
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      sections: [
        {
          title: '일반적인 문제',
          items: [
            { label: '인증 오류', content: 'APP ID와 Access Token(또는 API Key)이 불필요한 공백 없이 올바르게 복사되었는지, 그리고 Sokuji에서 선택한 자격 증명 종류가 입력한 것과 일치하는지 확인하세요.' },
            { label: '연결 오류', content: '네트워크 연결을 확인하고 방화벽이 WebSocket 연결을 차단하지 않는지 확인하세요. 프록시를 사용하는 경우 WebSocket을 지원하는지 확인하세요.' },
            { label: '지원되지 않는 언어 쌍', content: '선택한 소스 및 대상 언어 조합이 Doubao AST 2.0의 지원 범위 내에 있는지 확인하세요.' },
          ],
        },
        {
          title: '최상의 결과를 위한 팁',
          steps: [
            '최고의 번역 품질을 위해 안정적인 네트워크 연결 사용',
            '최적의 음성 인식을 위해 조용한 환경에서 사용',
            '번역 품질이 이상적이지 않은 경우 음성 모드 전환 시도',
            'Volcengine 콘솔에서 사용량과 할당량을 정기적으로 확인',
          ],
        },
      ],
    },
    helpText: '더 많은 도움이 필요하신가요? 공식 문서는 Volcengine 음성 기술 콘솔을 확인하시거나, GitHub 저장소에서 지원을 받으세요.',
    externalLinkText: 'Volcengine 음성 기술 콘솔',
  },
  es: {
    pageTitle: 'Tutorial de configuración de Doubao AST 2.0',
    backLink: 'Volver a proveedores de IA',
    overview: {
      title: 'Descripción general',
      content: 'Este tutorial le guiará en la configuración de Doubao AST 2.0 para su uso con Sokuji. Doubao AST 2.0 es un servicio de interpretación simultánea de voz a voz que puede clonar la voz del hablante o hablar con una voz fija elegida de su biblioteca.',
    },

    steps: [
      { title: 'Registre una cuenta en Volcengine', content: 'Vaya a <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> y regístrese para obtener una cuenta. Si ya tiene una cuenta de ByteDance/Volcengine, puede iniciar sesión directamente.' },
      { title: 'Navegue a la consola de voz', content: 'Después de iniciar sesión, seleccione <strong>"Doubao Simultaneous Interpreting 2.0"</strong> en el menú de la izquierda, o visite directamente la <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">consola de voz</a>.' },
      { title: 'Cree una aplicación', content: 'En la consola de voz, haga clic en <strong>"Create Application"</strong> para crear una nueva aplicación. Nómbrela (por ejemplo, "Sokuji Translation") y confirme la creación.' },
      { title: 'Obtenga APP ID y Access Token', content: 'Haga clic en <strong>"Doubao Simultaneous Interpreting 2.0"</strong> en el menú de la izquierda. Seleccione la aplicación recién creada en el menú desplegable de nombre de aplicación en la parte superior de la página, luego copie el <strong>APP ID</strong> y el <strong>Access Token</strong> que se muestran a continuación. Sokuji también acepta una API Key de Volcengine; sirve cualquiera de los dos tipos de credencial.', screenshot: true },
      { title: 'Configure en Sokuji', content: 'Abra Sokuji y seleccione <strong>"Doubao AST 2.0"</strong> como su proveedor de IA en el panel de configuración. Elija el tipo de credencial <strong>App ID + Access Token</strong> e ingrese su <strong>APP ID</strong> y <strong>Access Token</strong>, o elija <strong>Clave API</strong> y pegue la API Key. Después elija los idiomas de origen y destino; el Modo de habla (Auto, Pulsar para hablar o Pulsar para traducir) es un único ajuste para todos los proveedores.' },
      { title: 'Pruebe la conexión', content: 'Haga clic en el botón Iniciar sesión y hable por el micrófono para verificar que la traducción de voz a voz funciona. Debería ver la traducción. La traducción hablada va al micrófono virtual para la otra parte; para oírla usted también, active <strong>Yo también la oigo</strong> en el bloque <strong>Voz</strong> (configuración rápida, o Configuración → Avanzado → Audio).' },
    ],
    features: {
      title: 'Características',
      sections: [
        {
          title: '¿Qué es Doubao AST 2.0?',
          content: 'Doubao AST 2.0 es un servicio de traducción de voz de extremo a extremo:',
          items: [
            'Traducción simultánea de voz a voz sin pasos de texto intermedios',
            'Clonación de voz: la voz traducida conserva las características vocales del hablante',
            'Voces fijas: elija una por idioma de destino de la biblioteca de Doubao, filtrada por género, edad y estilo, con escucha previa',
            'Voz que yo oigo: con Traducción que yo oigo activada en el bloque Voz, la traducción de la otra parte también se le lee a usted, y Configuración de Voz muestra dos filas, Voz que oye la otra parte y Voz que yo oigo. Voz que yo oigo lista las voces de su propio idioma; allí, Clonar la voz del hablante clona a la otra persona. La voz se guarda por idioma, así que la voz que elija para un idioma lo lee en cualquiera de las dos filas. Con Chino + inglés, una sola fila sirve para ambas partes',
            'Procesamiento de traducción en tiempo real de baja latencia',
            'El chino y el inglés pueden traducirse en ambos sentidos en una misma sesión',
          ],
        },
        {
          title: 'Idiomas admitidos',
          content: 'Un lado de cada par de idiomas debe ser chino o inglés:',
          items: [
            'Hablados (voz a voz): chino, inglés, japonés, indonesio, español, portugués, alemán, francés; desde chino o inglés también se puede hablar coreano',
            'Solo texto: coreano, turco, malayo, neerlandés, rumano, polaco, checo, árabe, tailandés, vietnamita, ruso, italiano',
            'Dialectos: cantonés y wu (shanghainés), solo como idioma de origen',
            '“Chino + inglés”: ambos idiomas traducidos en cada sentido en una misma sesión',
          ],
        },
        {
          title: 'Modo de habla',
          items: [
            'Auto: el servicio detecta cuándo empieza y termina el habla, adecuado para conversaciones naturales',
            'Pulsar para hablar: el audio se envía solo mientras mantiene pulsada la barra espaciadora o el botón del micrófono, adecuado para entornos ruidosos o cuando se necesita control preciso',
            'Pulsar para traducir: igual que Pulsar para hablar, salvo que, mientras no mantiene pulsado, su propia voz va directamente al micrófono virtual',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      sections: [
        {
          title: 'Problemas comunes',
          items: [
            { label: 'Error de autenticación', content: 'Verifique que su APP ID y Access Token (o API Key) estén correctamente copiados sin espacios adicionales, y que el tipo de credencial seleccionado en Sokuji coincida con lo que ingresó.' },
            { label: 'Error de conexión', content: 'Compruebe su conexión de red y asegúrese de que los firewalls no bloqueen las conexiones WebSocket. Si usa un proxy, asegúrese de que admita WebSocket.' },
            { label: 'Par de idiomas no admitido', content: 'Confirme que la combinación de idiomas de origen y destino seleccionada esté dentro del rango admitido por Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Consejos para mejores resultados',
          steps: [
            'Use una conexión de red estable para obtener la mejor calidad de traducción',
            'Use en un ambiente tranquilo para un reconocimiento de voz óptimo',
            'Si la calidad de traducción no es ideal, intente cambiar el Modo de habla',
            'Verifique regularmente el uso y la cuota en la consola de Volcengine',
          ],
        },
      ],
    },
    helpText: '¿Necesita más ayuda? Visite la consola de voz de Volcengine para la documentación oficial o consulte nuestro repositorio de GitHub para obtener soporte.',
    externalLinkText: 'Consola de voz de Volcengine',
  },
  fr: {
    pageTitle: 'Tutoriel de configuration Doubao AST 2.0',
    backLink: 'Retour aux fournisseurs IA',
    overview: {
      title: 'Aperçu',
      content: 'Ce tutoriel vous guidera dans la configuration de Doubao AST 2.0 pour une utilisation avec Sokuji. Doubao AST 2.0 est un service d\'interprétation simultanée voix-à-voix qui peut cloner la voix du locuteur ou parler avec une voix fixe choisie dans sa bibliothèque.',
    },

    steps: [
      { title: 'Créez un compte Volcengine', content: 'Rendez-vous sur <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> et inscrivez-vous. Si vous avez déjà un compte ByteDance/Volcengine, vous pouvez vous connecter directement.' },
      { title: 'Accédez à la console vocale', content: 'Après connexion, sélectionnez <strong>"Doubao Simultaneous Interpreting 2.0"</strong> dans le menu de gauche, ou visitez directement la <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">console vocale</a>.' },
      { title: 'Créez une application', content: 'Dans la console vocale, cliquez sur <strong>"Create Application"</strong> pour créer une nouvelle application. Nommez-la (par ex. "Sokuji Translation") et confirmez la création.' },
      { title: 'Obtenez l\'APP ID et l\'Access Token', content: 'Cliquez sur <strong>"Doubao Simultaneous Interpreting 2.0"</strong> dans le menu de gauche. Sélectionnez votre application nouvellement créée dans le menu déroulant en haut de la page, puis copiez l\'<strong>APP ID</strong> et l\'<strong>Access Token</strong> affichés ci-dessous. Sokuji accepte aussi une clé API Volcengine ; l\'un ou l\'autre type d\'identifiant convient.', screenshot: true },
      { title: 'Configurez dans Sokuji', content: 'Ouvrez Sokuji et sélectionnez <strong>"Doubao AST 2.0"</strong> comme fournisseur IA dans le panneau des paramètres. Choisissez le type d\'identifiant <strong>App ID + Access Token</strong> et entrez votre <strong>APP ID</strong> et <strong>Access Token</strong>, ou choisissez <strong>Clé API</strong> et collez la clé API. Choisissez ensuite les langues source et cible ; le Mode de parole (Auto, Appuyer pour parler ou Appuyer pour traduire) est un seul réglage, commun à tous les fournisseurs.' },
      { title: 'Testez la connexion', content: 'Cliquez sur le bouton Démarrer la session et parlez dans votre microphone pour vérifier que la traduction voix-à-voix fonctionne. Vous devriez voir la traduction. La traduction parlée part dans le micro virtuel pour l\'autre ; pour l\'entendre vous-même, activez <strong>Je l\'entends aussi</strong> dans le bloc <strong>Voix</strong> (réglages Rapide, ou Paramètres → Avancé → Audio).' },
    ],
    features: {
      title: 'Fonctionnalités',
      sections: [
        {
          title: 'Qu\'est-ce que Doubao AST 2.0 ?',
          content: 'Doubao AST 2.0 est un service de traduction vocale de bout en bout :',
          items: [
            'Traduction simultanée voix-à-voix sans étapes de texte intermédiaires',
            'Clonage vocal : la voix traduite conserve les caractéristiques vocales du locuteur',
            'Voix fixes : choisissez-en une par langue cible dans la bibliothèque de Doubao, filtrée par genre, âge et style, avec un aperçu',
            'Voix que j\'entends : avec Traduction que j\'entends activée dans le bloc Voix, la traduction de l\'autre vous est lue aussi, et Paramètres de Voix affiche deux lignes, Voix entendue par l\'autre et Voix que j\'entends. Voix que j\'entends liste les voix de votre propre langue ; y choisir Cloner la voix de l\'orateur clone l\'autre personne. La voix est gardée par langue : celle que vous choisissez pour une langue la lit dans l\'une ou l\'autre ligne. Avec Chinois + anglais, une seule ligne sert aux deux côtés',
            'Traitement de traduction en temps réel à faible latence',
            'Le chinois et l\'anglais peuvent être traduits dans les deux sens en une seule session',
          ],
        },
        {
          title: 'Langues prises en charge',
          content: 'Un côté de chaque paire de langues doit être le chinois ou l\'anglais :',
          items: [
            'Parlées (voix-à-voix) : chinois, anglais, japonais, indonésien, espagnol, portugais, allemand, français ; depuis le chinois ou l\'anglais, le coréen peut aussi être parlé',
            'Texte uniquement : coréen, turc, malais, néerlandais, roumain, polonais, tchèque, arabe, thaï, vietnamien, russe, italien',
            'Dialectes : cantonais et wu (shanghaïen), comme langue source uniquement',
            '« Chinois + anglais » : les deux langues traduites dans chaque sens en une seule session',
          ],
        },
        {
          title: 'Mode de parole',
          items: [
            'Auto : le service détecte le début et la fin de la parole, adapté aux conversations naturelles',
            'Appuyer pour parler : l\'audio n\'est envoyé que pendant que vous maintenez Espace ou le bouton du microphone, adapté aux environnements bruyants ou lorsqu\'un contrôle précis est nécessaire',
            'Appuyer pour traduire : identique à Appuyer pour parler, sauf que, quand vous ne maintenez pas, votre propre voix va directement dans le microphone virtuel',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      sections: [
        {
          title: 'Problèmes courants',
          items: [
            { label: 'Erreur d\'authentification', content: 'Vérifiez que votre APP ID et Access Token (ou clé API) sont correctement copiés sans espaces supplémentaires, et que le type d\'identifiant sélectionné dans Sokuji correspond à ce que vous avez saisi.' },
            { label: 'Erreur de connexion', content: 'Vérifiez votre connexion réseau et assurez-vous que les pare-feu ne bloquent pas les connexions WebSocket. Si vous utilisez un proxy, assurez-vous qu\'il prend en charge WebSocket.' },
            { label: 'Paire de langues non prise en charge', content: 'Confirmez que la combinaison de langues source et cible sélectionnée est dans la plage prise en charge par Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Conseils pour de meilleurs résultats',
          steps: [
            'Utilisez une connexion réseau stable pour une meilleure qualité de traduction',
            'Utilisez dans un environnement calme pour une reconnaissance vocale optimale',
            'Si la qualité de traduction n\'est pas idéale, essayez de changer le Mode de parole',
            'Vérifiez régulièrement l\'utilisation et le quota dans la console Volcengine',
          ],
        },
      ],
    },
    helpText: 'Besoin d\'aide supplémentaire ? Visitez la console vocale Volcengine pour la documentation officielle ou consultez notre dépôt GitHub pour obtenir du support.',
    externalLinkText: 'Console vocale Volcengine',
  },
  ar: {
    pageTitle: 'دليل إعداد Doubao AST 2.0',
    backLink: 'العودة إلى مزودي الذكاء الاصطناعي',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد Doubao AST 2.0 للاستخدام مع Sokuji. Doubao AST 2.0 هي خدمة ترجمة فورية من صوت إلى صوت يمكنها استنساخ صوت المتحدث أو التحدث بصوت ثابت يُختار من مكتبتها.',
    },

    steps: [
      { title: 'سجّل حسابًا في Volcengine', content: 'انتقل إلى <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> وسجّل حسابًا. إذا كان لديك حساب ByteDance/Volcengine بالفعل، يمكنك تسجيل الدخول مباشرة.' },
      { title: 'انتقل إلى وحدة تحكم الصوت', content: 'بعد تسجيل الدخول، اختر <strong>"Doubao Simultaneous Interpreting 2.0"</strong> من القائمة اليسرى، أو قم بزيارة <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">وحدة تحكم الصوت</a> مباشرة.' },
      { title: 'أنشئ تطبيقًا', content: 'في وحدة تحكم الصوت، انقر على <strong>"Create Application"</strong> لإنشاء تطبيق جديد. قم بتسميته (مثل "Sokuji Translation") وأكد الإنشاء.' },
      { title: 'احصل على APP ID و Access Token', content: 'انقر على <strong>"Doubao Simultaneous Interpreting 2.0"</strong> في القائمة اليسرى. اختر التطبيق الذي أنشأته حديثًا من القائمة المنسدلة لاسم التطبيق أعلى الصفحة، ثم انسخ <strong>APP ID</strong> و <strong>Access Token</strong> المعروضين أدناه. يقبل Sokuji أيضًا مفتاح API من Volcengine؛ يكفي أي من نوعي بيانات الاعتماد.', screenshot: true },
      { title: 'قم بالتكوين في Sokuji', content: 'افتح Sokuji واختر <strong>"Doubao AST 2.0"</strong> كمزود الذكاء الاصطناعي في لوحة الإعدادات. اختر نوع بيانات الاعتماد <strong>App ID + Access Token</strong> وأدخل <strong>APP ID</strong> و <strong>Access Token</strong>، أو اختر <strong>مفتاح API</strong> والصق مفتاح API. ثم اختر لغتي المصدر والهدف؛ أما وضع الكلام (تلقائي أو اضغط للتحدث أو اضغط للترجمة) فهو إعداد واحد لجميع المزودين.' },
      { title: 'اختبر الاتصال', content: 'انقر على زر بدء الجلسة وتحدث في الميكروفون للتحقق من أن الترجمة الفورية من صوت إلى صوت تعمل. يجب أن ترى الترجمة. تذهب الترجمة المنطوقة إلى الميكروفون الافتراضي ليسمعها الطرف الآخر؛ لتسمعها أنت أيضًا، فعّل <strong>أسمعها أنا أيضًا</strong> في قسم <strong>الكلام</strong> (الإعدادات السريعة، أو الإعدادات ← متقدم ← الصوت).' },
    ],
    features: {
      title: 'الميزات',
      sections: [
        {
          title: 'ما هو Doubao AST 2.0؟',
          content: 'Doubao AST 2.0 هي خدمة ترجمة صوتية شاملة:',
          items: [
            'ترجمة فورية من صوت إلى صوت بدون خطوات نصية وسيطة',
            'استنساخ الصوت: الصوت المترجم يحتفظ بخصائص صوت المتحدث',
            'أصوات ثابتة: اختر صوتًا لكل لغة هدف من مكتبة Doubao، مع التصفية حسب الجنس والعمر والأسلوب، ومع إمكانية المعاينة',
            'الصوت الذي أسمعه: عند تفعيل «الترجمة التي أسمعها» في قسم الكلام، تُقرأ لك ترجمة الطرف الآخر أيضًا، وتعرض «إعدادات الصوت» صفين: «الصوت الذي يسمعه الطرف الآخر» و«الصوت الذي أسمعه». يعرض «الصوت الذي أسمعه» أصوات لغتك أنت، واختيار «استنساخ صوت المتحدث» فيه يستنسخ صوت الشخص الآخر. يُحفظ الصوت لكل لغة، فالصوت الذي تختاره للغة ما يقرؤها في أي من الصفين. مع «الصينية + الإنجليزية» يخدم صف واحد الطرفين',
            'معالجة ترجمة في الوقت الفعلي بزمن استجابة منخفض',
            'يمكن ترجمة الصينية والإنجليزية في الاتجاهين ضمن جلسة واحدة',
          ],
        },
        {
          title: 'اللغات المدعومة',
          content: 'يجب أن يكون أحد طرفي كل زوج لغات الصينية أو الإنجليزية:',
          items: [
            'منطوقة (من صوت إلى صوت): الصينية، الإنجليزية، اليابانية، الإندونيسية، الإسبانية، البرتغالية، الألمانية، الفرنسية؛ ومن الصينية أو الإنجليزية يمكن نطق الكورية أيضًا',
            'نص فقط: الكورية، التركية، الملايوية، الهولندية، الرومانية، البولندية، التشيكية، العربية، التايلاندية، الفيتنامية، الروسية، الإيطالية',
            'اللهجات: الكانتونية والوو (الشنغهائية)، كلغة مصدر فقط',
            '«الصينية + الإنجليزية»: تُترجم اللغتان في الاتجاهين ضمن جلسة واحدة',
          ],
        },
        {
          title: 'وضع الكلام',
          items: [
            'تلقائي: تكتشف الخدمة بداية الكلام ونهايته، مناسب للمحادثات الطبيعية',
            'اضغط للتحدث: لا يُرسَل الصوت إلا ما دمت تضغط على مفتاح المسافة أو زر الميكروفون، مناسب للبيئات الصاخبة أو عند الحاجة إلى تحكم دقيق',
            'اضغط للترجمة: مثل «اضغط للتحدث» تمامًا، إلا أن صوتك الأصلي يذهب مباشرة إلى الميكروفون الافتراضي حين لا تضغط',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      sections: [
        {
          title: 'المشاكل الشائعة',
          items: [
            { label: 'خطأ في المصادقة', content: 'تحقق من أن APP ID و Access Token (أو مفتاح API) منسوخة بشكل صحيح بدون مسافات إضافية، ومن أن نوع بيانات الاعتماد المحدد في Sokuji يطابق ما أدخلته.' },
            { label: 'خطأ في الاتصال', content: 'تحقق من اتصالك بالشبكة وتأكد من أن جدران الحماية لا تحظر اتصالات WebSocket. إذا كنت تستخدم وكيلًا، تأكد من أنه يدعم WebSocket.' },
            { label: 'زوج لغات غير مدعوم', content: 'تأكد من أن مجموعة لغتي المصدر والهدف المختارة ضمن النطاق المدعوم لـ Doubao AST 2.0.' },
          ],
        },
        {
          title: 'نصائح لأفضل النتائج',
          steps: [
            'استخدم اتصال شبكة مستقر للحصول على أفضل جودة ترجمة',
            'استخدم في بيئة هادئة للحصول على أفضل التعرف على الصوت',
            'إذا لم تكن جودة الترجمة مثالية، جرب تبديل وضع الكلام',
            'تحقق بانتظام من الاستخدام والحصة في وحدة تحكم Volcengine',
          ],
        },
      ],
    },
    helpText: 'هل تحتاج إلى مزيد من المساعدة؟ قم بزيارة وحدة تحكم الصوت في Volcengine للاطلاع على الوثائق الرسمية أو راجع مستودع GitHub الخاص بنا للحصول على الدعم.',
    externalLinkText: 'وحدة تحكم الصوت في Volcengine',
  },
  pt: {
    pageTitle: 'Tutorial de configuração do Doubao AST 2.0',
    backLink: 'Voltar aos provedores de IA',
    overview: {
      title: 'Visão geral',
      content: 'Este tutorial guiará você na configuração do Doubao AST 2.0 para uso com o Sokuji. O Doubao AST 2.0 é um serviço de interpretação simultânea de voz para voz que pode clonar a voz de quem fala ou falar com uma voz fixa escolhida da sua biblioteca.',
    },

    steps: [
      { title: 'Registre uma conta Volcengine', content: 'Acesse <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> e crie uma conta. Se você já tem uma conta ByteDance/Volcengine, pode fazer login diretamente.' },
      { title: 'Navegue até o console de voz', content: 'Após o login, selecione <strong>"Doubao Simultaneous Interpreting 2.0"</strong> no menu à esquerda, ou visite diretamente o <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">console de voz</a>.' },
      { title: 'Crie um aplicativo', content: 'No console de voz, clique em <strong>"Create Application"</strong> para criar um novo aplicativo. Nomeie-o (por exemplo, "Sokuji Translation") e confirme a criação.' },
      { title: 'Obtenha o APP ID e o Access Token', content: 'Clique em <strong>"Doubao Simultaneous Interpreting 2.0"</strong> no menu à esquerda. Selecione o aplicativo recém-criado no menu suspenso de nome do aplicativo no topo da página, depois copie o <strong>APP ID</strong> e o <strong>Access Token</strong> exibidos abaixo. O Sokuji também aceita uma API Key da Volcengine; qualquer um dos dois tipos de credencial serve.', screenshot: true },
      { title: 'Configure no Sokuji', content: 'Abra o Sokuji e selecione <strong>"Doubao AST 2.0"</strong> como seu provedor de IA no painel de configurações. Escolha o tipo de credencial <strong>App ID + Access Token</strong> e insira seu <strong>APP ID</strong> e <strong>Access Token</strong>, ou escolha <strong>Chave de API</strong> e cole a API Key. Depois escolha os idiomas de origem e destino; o Modo de fala (Auto, Pressionar para falar ou Pressionar para traduzir) é uma única configuração para todos os provedores.' },
      { title: 'Teste a conexão', content: 'Clique no botão Iniciar sessão e fale no microfone para verificar se a tradução de voz para voz está funcionando. Você deverá ver a tradução. A tradução falada vai para o microfone virtual, para o outro lado; para ouvi-la também, ative <strong>Eu também ouço</strong> no bloco <strong>Voz</strong> (configurações rápidas, ou Configurações → Avançado → Áudio).' },
    ],
    features: {
      title: 'Recursos',
      sections: [
        {
          title: 'O que é o Doubao AST 2.0?',
          content: 'O Doubao AST 2.0 é um serviço de tradução de voz ponta a ponta:',
          items: [
            'Tradução simultânea de voz para voz sem etapas de texto intermediárias',
            'Clonagem de voz: a voz traduzida preserva as características vocais do falante',
            'Vozes fixas: escolha uma por idioma de destino na biblioteca do Doubao, filtrando por gênero, idade e estilo, com prévia',
            'Voz que eu ouço: com Tradução que eu ouço ativada no bloco Voz, a tradução do outro lado também é lida para você, e Configurações de Voz mostra duas linhas, Voz que o outro lado ouve e Voz que eu ouço. Voz que eu ouço lista as vozes do seu próprio idioma; ali, Clonar a voz de quem fala clona a outra pessoa. A voz fica guardada por idioma, então a voz que você escolhe para um idioma o lê em qualquer uma das duas linhas. Com Chinês + Inglês, uma só linha serve aos dois lados',
            'Processamento de tradução em tempo real de baixa latência',
            'Chinês e inglês podem ser traduzidos nos dois sentidos em uma mesma sessão',
          ],
        },
        {
          title: 'Idiomas suportados',
          content: 'Um dos lados de cada par de idiomas precisa ser chinês ou inglês:',
          items: [
            'Falados (voz para voz): chinês, inglês, japonês, indonésio, espanhol, português, alemão, francês; a partir do chinês ou do inglês, o coreano também pode ser falado',
            'Somente texto: coreano, turco, malaio, holandês, romeno, polonês, tcheco, árabe, tailandês, vietnamita, russo, italiano',
            'Dialetos: cantonês e wu (xangainês), apenas como idioma de origem',
            '“Chinês + Inglês”: os dois idiomas traduzidos nos dois sentidos em uma mesma sessão',
          ],
        },
        {
          title: 'Modo de fala',
          items: [
            'Auto: o serviço detecta quando a fala começa e termina, adequado para conversas naturais',
            'Pressionar para falar: o áudio é enviado só enquanto você mantém pressionada a tecla Espaço ou o botão do microfone, adequado para ambientes barulhentos ou quando é preciso controle preciso',
            'Pressionar para traduzir: igual a Pressionar para falar, exceto que, enquanto você não está pressionando, a sua própria voz vai direto para o microfone virtual',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      sections: [
        {
          title: 'Problemas comuns',
          items: [
            { label: 'Erro de autenticação', content: 'Verifique se o APP ID e o Access Token (ou a API Key) foram copiados corretamente sem espaços extras, e se o tipo de credencial selecionado no Sokuji corresponde ao que você inseriu.' },
            { label: 'Erro de conexão', content: 'Verifique sua conexão de rede e certifique-se de que os firewalls não estão bloqueando conexões WebSocket. Se estiver usando um proxy, certifique-se de que ele suporta WebSocket.' },
            { label: 'Par de idiomas não suportado', content: 'Confirme que a combinação de idiomas de origem e destino selecionada está dentro do alcance suportado pelo Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Dicas para melhores resultados',
          steps: [
            'Use uma conexão de rede estável para obter a melhor qualidade de tradução',
            'Use em um ambiente silencioso para reconhecimento de voz ideal',
            'Se a qualidade da tradução não for ideal, tente mudar o Modo de fala',
            'Verifique regularmente o uso e a cota no console Volcengine',
          ],
        },
      ],
    },
    helpText: 'Precisa de mais ajuda? Visite o console de voz da Volcengine para documentação oficial ou confira nosso repositório GitHub para suporte.',
    externalLinkText: 'Console de voz da Volcengine',
  },
  ru: {
    pageTitle: 'Руководство по настройке Doubao AST 2.0',
    backLink: 'Назад к провайдерам ИИ',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить Doubao AST 2.0 для использования с Sokuji. Doubao AST 2.0 — это сервис синхронного перевода речи в речь, который может клонировать голос говорящего или говорить фиксированным голосом, выбранным из его библиотеки.',
    },

    steps: [
      { title: 'Зарегистрируйте аккаунт Volcengine', content: 'Перейдите на <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> и зарегистрируйте аккаунт. Если у вас уже есть аккаунт ByteDance/Volcengine, вы можете войти напрямую.' },
      { title: 'Перейдите в консоль речевых технологий', content: 'После входа выберите <strong>"Doubao Simultaneous Interpreting 2.0"</strong> в левом меню или перейдите напрямую в <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">консоль речевых технологий</a>.' },
      { title: 'Создайте приложение', content: 'В консоли речевых технологий нажмите <strong>"Create Application"</strong>, чтобы создать новое приложение. Назовите его (например, "Sokuji Translation") и подтвердите создание.' },
      { title: 'Получите APP ID и Access Token', content: 'Нажмите <strong>"Doubao Simultaneous Interpreting 2.0"</strong> в левом меню. Выберите только что созданное приложение из выпадающего списка имён приложений вверху страницы, затем скопируйте <strong>APP ID</strong> и <strong>Access Token</strong>, показанные ниже. Sokuji также принимает API Key Volcengine; подойдёт любой из двух видов учётных данных.', screenshot: true },
      { title: 'Настройте в Sokuji', content: 'Откройте Sokuji и выберите <strong>"Doubao AST 2.0"</strong> в качестве провайдера ИИ в панели настроек. Выберите вид учётных данных <strong>App ID + Access Token</strong> и введите свои <strong>APP ID</strong> и <strong>Access Token</strong> либо выберите <strong>API-ключ</strong> и вставьте API Key. Затем выберите исходный и целевой языки; режим речи («Авто», «Нажми и говори» или «Нажми и переведи») — одна настройка для всех провайдеров.' },
      { title: 'Протестируйте соединение', content: 'Нажмите кнопку «Начать сеанс» и говорите в микрофон, чтобы убедиться, что перевод речи в речь работает. Вы должны увидеть перевод. Озвученный перевод уходит в виртуальный микрофон для собеседника; чтобы слышать его самому, включите <strong>Я тоже слышу</strong> в блоке <strong>Речь</strong> (быстрые настройки или Настройки → Расширенный → Аудио).' },
    ],
    features: {
      title: 'Функции',
      sections: [
        {
          title: 'Что такое Doubao AST 2.0?',
          content: 'Doubao AST 2.0 — это сквозной сервис перевода речи:',
          items: [
            'Синхронный перевод речи в речь без промежуточных текстовых этапов',
            'Клонирование голоса: переведённая речь сохраняет голосовые характеристики говорящего',
            'Фиксированные голоса: выберите по одному на каждый целевой язык из библиотеки Doubao, с фильтрами по полу, возрасту и стилю и с прослушиванием',
            'Голос, который слышу я: если в блоке «Речь» включён «Перевод, который слышу я», перевод собеседника тоже читается вам, а в «Настройки голоса» появляются две строки: «Голос, который слышит собеседник» и «Голос, который слышу я». В строке «Голос, который слышу я» перечислены голоса вашего языка; выбор «Клонировать голос говорящего» там клонирует собеседника. Голос сохраняется для каждого языка, поэтому голос, выбранный для языка, читает его в любой из двух строк. Для «Китайский + английский» одна строка служит обеим сторонам',
            'Обработка перевода в реальном времени с низкой задержкой',
            'Китайский и английский можно переводить в обе стороны в одном сеансе',
          ],
        },
        {
          title: 'Поддерживаемые языки',
          content: 'Одной из сторон каждой языковой пары должен быть китайский или английский:',
          items: [
            'Озвучиваемые (речь в речь): китайский, английский, японский, индонезийский, испанский, португальский, немецкий, французский; с китайского или английского озвучивается и корейский',
            'Только текст: корейский, турецкий, малайский, нидерландский, румынский, польский, чешский, арабский, тайский, вьетнамский, русский, итальянский',
            'Диалекты: кантонский и у (шанхайский) — только как исходный язык',
            '«Китайский + английский»: оба языка переводятся в обе стороны в одном сеансе',
          ],
        },
        {
          title: 'Режим речи',
          items: [
            'Авто: сервис определяет начало и конец речи, подходит для естественных разговоров',
            'Нажми и говори: звук отправляется только пока вы удерживаете пробел или кнопку микрофона, подходит для шумных сред или когда нужен точный контроль',
            'Нажми и переведи: то же, что «Нажми и говори», только пока вы не удерживаете, ваш собственный голос идёт напрямую на виртуальный микрофон',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      sections: [
        {
          title: 'Частые проблемы',
          items: [
            { label: 'Ошибка аутентификации', content: 'Убедитесь, что APP ID и Access Token (или API Key) правильно скопированы без лишних пробелов и что вид учётных данных, выбранный в Sokuji, соответствует тому, что вы ввели.' },
            { label: 'Ошибка подключения', content: 'Проверьте сетевое подключение и убедитесь, что брандмауэры не блокируют WebSocket-соединения. Если используете прокси, убедитесь, что он поддерживает WebSocket.' },
            { label: 'Неподдерживаемая языковая пара', content: 'Подтвердите, что выбранная комбинация исходного и целевого языков находится в пределах поддерживаемого диапазона Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Советы для лучших результатов',
          steps: [
            'Используйте стабильное сетевое подключение для лучшего качества перевода',
            'Используйте в тихой обстановке для оптимального распознавания речи',
            'Если качество перевода неидеально, попробуйте переключить режим речи',
            'Регулярно проверяйте использование и квоту в консоли Volcengine',
          ],
        },
      ],
    },
    helpText: 'Нужна дополнительная помощь? Посетите консоль речевых технологий Volcengine для ознакомления с официальной документацией или наш репозиторий GitHub для получения поддержки.',
    externalLinkText: 'Консоль речевых технологий Volcengine',
  },
  de: {
    pageTitle: 'Doubao AST 2.0 Einrichtungstutorial',
    backLink: 'Zurück zu KI-Anbietern',
    overview: {
      title: 'Übersicht',
      content: 'Dieses Tutorial führt Sie durch die Einrichtung von Doubao AST 2.0 für die Verwendung mit Sokuji. Doubao AST 2.0 ist ein Sprache-zu-Sprache-Simultandolmetschdienst, der die Stimme des Sprechers klonen oder in einer festen Stimme aus seiner Bibliothek sprechen kann.',
    },

    steps: [
      { title: 'Registrieren Sie ein Volcengine-Konto', content: 'Gehen Sie zu <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> und registrieren Sie sich. Wenn Sie bereits ein ByteDance/Volcengine-Konto haben, können Sie sich direkt anmelden.' },
      { title: 'Navigieren Sie zur Sprachkonsole', content: 'Nach der Anmeldung wählen Sie <strong>"Doubao Simultaneous Interpreting 2.0"</strong> im linken Menü oder besuchen Sie direkt die <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">Sprachkonsole</a>.' },
      { title: 'Erstellen Sie eine Anwendung', content: 'In der Sprachkonsole klicken Sie auf <strong>"Create Application"</strong>, um eine neue Anwendung zu erstellen. Benennen Sie sie (z.B. "Sokuji Translation") und bestätigen Sie die Erstellung.' },
      { title: 'APP ID und Access Token erhalten', content: 'Klicken Sie auf <strong>"Doubao Simultaneous Interpreting 2.0"</strong> im linken Menü. Wählen Sie Ihre neu erstellte Anwendung aus dem Dropdown-Menü für Anwendungsnamen oben auf der Seite, kopieren Sie dann die <strong>APP ID</strong> und den <strong>Access Token</strong>, die unten angezeigt werden. Sokuji akzeptiert auch einen Volcengine API Key; beide Arten von Zugangsdaten funktionieren.', screenshot: true },
      { title: 'Konfigurieren Sie in Sokuji', content: 'Öffnen Sie Sokuji und wählen Sie <strong>"Doubao AST 2.0"</strong> als Ihren KI-Anbieter im Einstellungspanel. Wählen Sie als Art der Zugangsdaten <strong>App ID + Access Token</strong> und geben Sie Ihre <strong>APP ID</strong> und Ihren <strong>Access Token</strong> ein, oder wählen Sie <strong>API-Schlüssel</strong> und fügen Sie den API Key ein. Wählen Sie dann Quell- und Zielsprache; der Sprachmodus (Auto, Push-to-Talk oder Push-to-Translate) ist eine Einstellung für alle Anbieter.' },
      { title: 'Testen Sie die Verbindung', content: 'Klicken Sie auf die Schaltfläche Sitzung starten und sprechen Sie in Ihr Mikrofon, um zu überprüfen, ob die Sprache-zu-Sprache-Übersetzung funktioniert. Sie sollten die Übersetzung sehen. Die gesprochene Übersetzung geht für das Gegenüber ins virtuelle Mikrofon; um sie selbst zu hören, schalten Sie im Block <strong>Sprache</strong> (Schnell-Einstellungen oder Einstellungen → Erweitert → Audio) <strong>Ich höre es auch</strong> ein.' },
    ],
    features: {
      title: 'Funktionen',
      sections: [
        {
          title: 'Was ist Doubao AST 2.0?',
          content: 'Doubao AST 2.0 ist ein Ende-zu-Ende-Sprachübersetzungsdienst:',
          items: [
            'Sprache-zu-Sprache-Simultanübersetzung ohne Zwischentextschritte',
            'Stimmklonen: Die übersetzte Sprache behält die Stimmcharakteristiken des Sprechers bei',
            'Feste Stimmen: Wählen Sie pro Zielsprache eine aus der Doubao-Bibliothek, gefiltert nach Geschlecht, Alter und Stil, mit Vorschau',
            'Stimme, die ich höre: Ist im Block Sprache Übersetzung, die ich höre, eingeschaltet, wird Ihnen auch die Übersetzung des Gegenübers vorgelesen, und die Stimmeinstellungen zeigen zwei Zeilen: Stimme, die das Gegenüber hört, und Stimme, die ich höre. Stimme, die ich höre, listet die Stimmen Ihrer eigenen Sprache; Stimme des Sprechers klonen klont dort die andere Person. Die Stimme wird pro Sprache gespeichert: Eine für eine Sprache gewählte Stimme liest diese Sprache in beiden Zeilen. Bei Chinesisch + Englisch dient eine Zeile beiden Seiten',
            'Echtzeit-Übersetzungsverarbeitung mit niedriger Latenz',
            'Chinesisch und Englisch können in einer Sitzung in beide Richtungen übersetzt werden',
          ],
        },
        {
          title: 'Unterstützte Sprachen',
          content: 'Eine Seite jedes Sprachpaars muss Chinesisch oder Englisch sein:',
          items: [
            'Gesprochen (Sprache-zu-Sprache): Chinesisch, Englisch, Japanisch, Indonesisch, Spanisch, Portugiesisch, Deutsch, Französisch; aus dem Chinesischen oder Englischen kann auch Koreanisch gesprochen werden',
            'Nur Text: Koreanisch, Türkisch, Malaiisch, Niederländisch, Rumänisch, Polnisch, Tschechisch, Arabisch, Thai, Vietnamesisch, Russisch, Italienisch',
            'Dialekte: Kantonesisch und Wu (Shanghaiisch), nur als Quellsprache',
            '„Chinesisch + Englisch“: beide Sprachen werden in einer Sitzung in beide Richtungen übersetzt',
          ],
        },
        {
          title: 'Sprachmodus',
          items: [
            'Auto: Der Dienst erkennt, wann Sprache beginnt und endet, geeignet für natürliche Gespräche',
            'Push-to-Talk: Audio wird nur gesendet, solange Sie die Leertaste oder die Mikrofon-Schaltfläche gedrückt halten, geeignet für laute Umgebungen oder wenn präzise Kontrolle benötigt wird',
            'Push-to-Translate: wie Push-to-Talk, nur dass Ihre eigene Stimme, solange Sie nicht drücken, direkt an das virtuelle Mikrofon geht',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      sections: [
        {
          title: 'Häufige Probleme',
          items: [
            { label: 'Authentifizierungsfehler', content: 'Überprüfen Sie, ob APP ID und Access Token (oder API Key) korrekt kopiert wurden, ohne zusätzliche Leerzeichen, und ob die in Sokuji gewählte Art der Zugangsdaten zu dem passt, was Sie eingegeben haben.' },
            { label: 'Verbindungsfehler', content: 'Überprüfen Sie Ihre Netzwerkverbindung und stellen Sie sicher, dass Firewalls WebSocket-Verbindungen nicht blockieren. Bei Verwendung eines Proxys stellen Sie sicher, dass er WebSocket unterstützt.' },
            { label: 'Nicht unterstütztes Sprachpaar', content: 'Bestätigen Sie, dass die ausgewählte Quell- und Zielsprachkombination im unterstützten Bereich von Doubao AST 2.0 liegt.' },
          ],
        },
        {
          title: 'Tipps für beste Ergebnisse',
          steps: [
            'Verwenden Sie eine stabile Netzwerkverbindung für beste Übersetzungsqualität',
            'Verwenden Sie es in einer ruhigen Umgebung für optimale Spracherkennung',
            'Wenn die Übersetzungsqualität nicht ideal ist, versuchen Sie, den Sprachmodus zu wechseln',
            'Überprüfen Sie regelmäßig die Nutzung und das Kontingent in der Volcengine-Konsole',
          ],
        },
      ],
    },
    helpText: 'Benötigen Sie weitere Hilfe? Besuchen Sie die Volcengine-Sprachkonsole für offizielle Dokumentation oder unser GitHub-Repository für Support.',
    externalLinkText: 'Volcengine-Sprachkonsole',
  },
  uk: {
    pageTitle: 'Посібник з налаштування Doubao AST 2.0',
    backLink: 'Назад до провайдерів ШІ',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати Doubao AST 2.0 для використання з Sokuji. Doubao AST 2.0 — це сервіс синхронного перекладу мовлення в мовлення, який може клонувати голос мовця або говорити фіксованим голосом, вибраним із його бібліотеки.',
    },

    steps: [
      { title: 'Зареєструйте обліковий запис Volcengine', content: 'Перейдіть на <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> та зареєструйте обліковий запис. Якщо у вас вже є обліковий запис ByteDance/Volcengine, ви можете увійти напряму.' },
      { title: 'Перейдіть до консолі мовлення', content: 'Після входу оберіть <strong>"Doubao Simultaneous Interpreting 2.0"</strong> у лівому меню або перейдіть безпосередньо до <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">консолі мовлення</a>.' },
      { title: 'Створіть застосунок', content: 'У консолі мовлення натисніть <strong>"Create Application"</strong>, щоб створити новий застосунок. Назвіть його (наприклад, "Sokuji Translation") та підтвердіть створення.' },
      { title: 'Отримайте APP ID та Access Token', content: 'Натисніть <strong>"Doubao Simultaneous Interpreting 2.0"</strong> у лівому меню. Оберіть щойно створений застосунок зі спадного списку назв застосунків угорі сторінки, потім скопіюйте <strong>APP ID</strong> та <strong>Access Token</strong>, показані нижче. Sokuji також приймає API Key від Volcengine; підійде будь-який із двох видів облікових даних.', screenshot: true },
      { title: 'Налаштуйте в Sokuji', content: 'Відкрийте Sokuji та оберіть <strong>"Doubao AST 2.0"</strong> як свого провайдера ШІ в панелі налаштувань. Виберіть вид облікових даних <strong>App ID + Access Token</strong> і введіть свої <strong>APP ID</strong> та <strong>Access Token</strong>, або виберіть <strong>API-ключ</strong> і вставте API Key. Потім оберіть мови джерела та цілі; «Режим мовлення» («Авто», «Натисни і говори» або «Натисни і переклади») — одне налаштування для всіх провайдерів.' },
      { title: 'Протестуйте з\'єднання', content: 'Натисніть кнопку «Почати сеанс» та говоріть у мікрофон, щоб перевірити, що переклад мовлення в мовлення працює. Ви повинні побачити переклад. Озвучений переклад іде у віртуальний мікрофон для співрозмовника; щоб чути його самому, увімкніть <strong>Я теж чую</strong> у блоці <strong>Мовлення</strong> (швидкі налаштування або Налаштування → Розширений → Аудіо).' },
    ],
    features: {
      title: 'Функції',
      sections: [
        {
          title: 'Що таке Doubao AST 2.0?',
          content: 'Doubao AST 2.0 — це наскрізний сервіс перекладу мовлення:',
          items: [
            'Синхронний переклад мовлення в мовлення без проміжних текстових етапів',
            'Клонування голосу: перекладене мовлення зберігає голосові характеристики мовця',
            'Фіксовані голоси: виберіть по одному на кожну цільову мову з бібліотеки Doubao, з фільтром за статтю, віком і стилем та з попереднім прослуховуванням',
            'Голос, який чую я: якщо в блоці «Мовлення» ввімкнено «Переклад, який чую я», переклад співрозмовника теж читається вам, а в «Налаштування голосу» з’являються два рядки: «Голос, який чує співрозмовник» і «Голос, який чую я». У рядку «Голос, який чую я» перелічено голоси вашої мови; вибір «Клонувати голос мовця» там клонує співрозмовника. Голос зберігається для кожної мови, тож голос, обраний для мови, читає її в будь-якому з двох рядків. Для «Китайська + англійська» один рядок слугує обом сторонам',
            'Обробка перекладу в реальному часі з низькою затримкою',
            'Китайську та англійську можна перекладати в обидва боки в одному сеансі',
          ],
        },
        {
          title: 'Підтримувані мови',
          content: 'Однією зі сторін кожної мовної пари має бути китайська або англійська:',
          items: [
            'Озвучуються (мовлення в мовлення): китайська, англійська, японська, індонезійська, іспанська, португальська, німецька, французька; з китайської або англійської можна озвучити й корейську',
            'Лише текст: корейська, турецька, малайська, нідерландська, румунська, польська, чеська, арабська, тайська, в\'єтнамська, російська, італійська',
            'Діалекти: кантонська та у (шанхайська), лише як мова джерела',
            '«Китайська + англійська»: обидві мови перекладаються в обидва боки в одному сеансі',
          ],
        },
        {
          title: 'Режим мовлення',
          items: [
            'Авто: сервіс виявляє початок і кінець мовлення, підходить для природних розмов',
            'Натисни і говори: аудіо надсилається лише поки ви тримаєте пробіл або кнопку мікрофона, підходить для шумних середовищ або коли потрібен точний контроль',
            'Натисни і переклади: те саме, що «Натисни і говори», тільки поки ви не тримаєте, ваш власний голос іде прямо на віртуальний мікрофон',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      sections: [
        {
          title: 'Поширені проблеми',
          items: [
            { label: 'Помилка автентифікації', content: 'Переконайтеся, що APP ID та Access Token (або API Key) правильно скопійовані без зайвих пробілів і що вид облікових даних, вибраний у Sokuji, відповідає тому, що ви ввели.' },
            { label: 'Помилка з\'єднання', content: 'Перевірте мережеве підключення та переконайтеся, що брандмауери не блокують WebSocket-з\'єднання. Якщо використовуєте проксі, переконайтеся, що він підтримує WebSocket.' },
            { label: 'Непідтримувана мовна пара', content: 'Підтвердіть, що обрана комбінація мов джерела та цілі знаходиться в межах підтримуваного діапазону Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Поради для найкращих результатів',
          steps: [
            'Використовуйте стабільне мережеве підключення для найкращої якості перекладу',
            'Використовуйте в тихому середовищі для оптимального розпізнавання мовлення',
            'Якщо якість перекладу не ідеальна, спробуйте перемкнути «Режим мовлення»',
            'Регулярно перевіряйте використання та квоту в консолі Volcengine',
          ],
        },
      ],
    },
    helpText: 'Потрібна додаткова допомога? Відвідайте консоль мовлення Volcengine для офіційної документації або наш репозиторій GitHub для підтримки.',
    externalLinkText: 'Консоль мовлення Volcengine',
  },
  it: {
    pageTitle: 'Tutorial di configurazione Doubao AST 2.0',
    backLink: 'Torna ai fornitori di IA',
    overview: {
      title: 'Panoramica',
      content: 'Questo tutorial ti guiderà nella configurazione di Doubao AST 2.0 per l\'uso con Sokuji. Doubao AST 2.0 è un servizio di interpretazione simultanea voce-a-voce che può clonare la voce di chi parla o parlare con una voce fissa scelta dalla sua libreria.',
    },

    steps: [
      { title: 'Registra un account Volcengine', content: 'Vai su <a href="https://www.volcengine.com" target="_blank" rel="noopener noreferrer">volcengine.com</a> e registrati. Se hai già un account ByteDance/Volcengine, puoi accedere direttamente.' },
      { title: 'Naviga alla console vocale', content: 'Dopo l\'accesso, seleziona <strong>"Doubao Simultaneous Interpreting 2.0"</strong> dal menu a sinistra, o visita direttamente la <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noopener noreferrer">console vocale</a>.' },
      { title: 'Crea un\'applicazione', content: 'Nella console vocale, clicca su <strong>"Create Application"</strong> per creare una nuova applicazione. Assegnale un nome (ad es. "Sokuji Translation") e conferma la creazione.' },
      { title: 'Ottieni APP ID e Access Token', content: 'Clicca su <strong>"Doubao Simultaneous Interpreting 2.0"</strong> nel menu a sinistra. Seleziona l\'applicazione appena creata dal menu a discesa dei nomi delle applicazioni in cima alla pagina, poi copia l\'<strong>APP ID</strong> e l\'<strong>Access Token</strong> mostrati sotto. Sokuji accetta anche una chiave API Volcengine; va bene uno qualsiasi dei due tipi di credenziale.', screenshot: true },
      { title: 'Configura in Sokuji', content: 'Apri Sokuji e seleziona <strong>"Doubao AST 2.0"</strong> come fornitore di IA nel pannello impostazioni. Scegli il tipo di credenziale <strong>App ID + Access Token</strong> e inserisci il tuo <strong>APP ID</strong> e <strong>Access Token</strong>, oppure scegli <strong>Chiave API</strong> e incolla l\'API Key. Poi scegli le lingue di origine e destinazione; la Modalità vocale (Auto, Premi per parlare o Premi per tradurre) è un\'unica impostazione per tutti i fornitori.' },
      { title: 'Testa la connessione', content: 'Clicca il pulsante Avvia sessione e parla nel microfono per verificare che la traduzione voce-a-voce funzioni. Dovresti vedere la traduzione. La traduzione parlata va nel microfono virtuale per l\'altra parte; per sentirla anche tu, attiva <strong>Lo sento anche io</strong> nel blocco <strong>Voce</strong> (impostazioni rapide, oppure Impostazioni → Avanzato → Audio).' },
    ],
    features: {
      title: 'Funzionalità',
      sections: [
        {
          title: 'Cos\'è Doubao AST 2.0?',
          content: 'Doubao AST 2.0 è un servizio di traduzione vocale end-to-end:',
          items: [
            'Traduzione simultanea voce-a-voce senza passaggi di testo intermedi',
            'Clonazione vocale: la voce tradotta preserva le caratteristiche vocali del parlante',
            'Voci fisse: scegline una per ogni lingua di destinazione dalla libreria di Doubao, filtrando per genere, età e stile, con anteprima',
            'Voce che sento io: con Traduzione che sento io attiva nel blocco Voce, anche la traduzione dell\'altra parte viene letta a te, e Impostazioni Voce mostra due righe, Voce che sente l\'altra parte e Voce che sento io. Voce che sento io elenca le voci della tua lingua; lì, Clona la voce di chi parla clona l\'altra persona. La voce viene salvata per lingua, quindi la voce che scegli per una lingua la legge in entrambe le righe. Con Cinese + inglese, una sola riga vale per entrambe le parti',
            'Elaborazione della traduzione in tempo reale a bassa latenza',
            'Cinese e inglese possono essere tradotti in entrambe le direzioni in una sola sessione',
          ],
        },
        {
          title: 'Lingue supportate',
          content: 'Un lato di ogni coppia di lingue deve essere il cinese o l\'inglese:',
          items: [
            'Parlate (voce-a-voce): cinese, inglese, giapponese, indonesiano, spagnolo, portoghese, tedesco, francese; dal cinese o dall\'inglese si può parlare anche il coreano',
            'Solo testo: coreano, turco, malese, olandese, rumeno, polacco, ceco, arabo, thailandese, vietnamita, russo, italiano',
            'Dialetti: cantonese e wu (shanghainese), solo come lingua di origine',
            '“Cinese + inglese”: entrambe le lingue tradotte in entrambe le direzioni in una sola sessione',
          ],
        },
        {
          title: 'Modalità vocale',
          items: [
            'Auto: il servizio rileva quando il parlato inizia e finisce, adatta a conversazioni naturali',
            'Premi per parlare: l\'audio viene inviato solo mentre tieni premuto Spazio o il pulsante del microfono, adatta ad ambienti rumorosi o quando è necessario un controllo preciso',
            'Premi per tradurre: uguale a Premi per parlare, salvo che mentre non tieni premuto la tua voce va direttamente nel microfono virtuale',
          ],
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      sections: [
        {
          title: 'Problemi comuni',
          items: [
            { label: 'Errore di autenticazione', content: 'Verifica che APP ID e Access Token (o API Key) siano stati copiati correttamente senza spazi aggiuntivi, e che il tipo di credenziale selezionato in Sokuji corrisponda a quello che hai inserito.' },
            { label: 'Errore di connessione', content: 'Controlla la connessione di rete e assicurati che i firewall non blocchino le connessioni WebSocket. Se usi un proxy, assicurati che supporti WebSocket.' },
            { label: 'Coppia di lingue non supportata', content: 'Conferma che la combinazione di lingue di origine e destinazione selezionata rientri nell\'intervallo supportato da Doubao AST 2.0.' },
          ],
        },
        {
          title: 'Suggerimenti per i migliori risultati',
          steps: [
            'Usa una connessione di rete stabile per la migliore qualità di traduzione',
            'Usa in un ambiente silenzioso per un riconoscimento vocale ottimale',
            'Se la qualità della traduzione non è ideale, prova a cambiare la Modalità vocale',
            'Controlla regolarmente l\'utilizzo e la quota nella console Volcengine',
          ],
        },
      ],
    },
    helpText: 'Hai bisogno di ulteriore aiuto? Visita la console vocale Volcengine per la documentazione ufficiale o il nostro repository GitHub per supporto.',
    externalLinkText: 'Console vocale Volcengine',
  },
};

export function VolcengineAST2Setup() {
  const { locale } = useI18n();
  const t = translations[locale] || translations.en;
  useDocumentMeta(t.pageTitle, t.overview.content);
  const [lightboxImage, setLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  return (
    <div className="docs-content tutorial-page">
      <Link to={localizePath('/docs/ai-providers', locale)} className="tutorial-page__back-link">
        <ArrowLeft size={16} />
        {t.backLink}
      </Link>

      <h1>{t.pageTitle}</h1>

      <section className="tutorial-page__section">
        <h2>{t.overview.title}</h2>
        <p>{t.overview.content}</p>
      </section>

      <section className="tutorial-page__section">
        {t.steps.map((step, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>
              <span className="tutorial-page__step-number" style={{ backgroundColor: ACCENT_COLOR }}>{index + 1}</span>
              {step.title}
            </h3>
            <p dangerouslySetInnerHTML={{ __html: step.content }} />
            {step.screenshot && (
              <img
                src="/tutorials/volcengine-ast2-setup/1.png"
                alt="APP ID and Access Token"
                className="tutorial-page__screenshot"
                onClick={() => setLightboxImage({ src: '/tutorials/volcengine-ast2-setup/1.png', alt: 'APP ID and Access Token' })}
              />
            )}
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <h2>{t.features.title}</h2>
        {t.features.sections.map((section, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>{section.title}</h3>
            {section.content && <p>{section.content}</p>}
            <ul>
              {section.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <h2>{t.troubleshooting.title}</h2>
        {t.troubleshooting.sections.map((section, index) => (
          <div key={index} className="tutorial-page__step" style={{ borderLeftColor: ACCENT_COLOR }}>
            <h3>{section.title}</h3>
            {section.items && section.items.map((item, i) => (
              <p key={i}><strong>{item.label}:</strong> {item.content}</p>
            ))}
            {section.steps && (
              <ol>
                {section.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            )}
          </div>
        ))}
      </section>

      <section className="tutorial-page__section">
        <div className="tutorial-page__tip">
          <p>{t.helpText}</p>
        </div>
      </section>

      <section className="tutorial-page__section">
        <a
          href="https://console.volcengine.com/speech/app"
          target="_blank"
          rel="noopener noreferrer"
          className="install-page__download-btn"
          style={{ backgroundColor: ACCENT_COLOR }}
        >
          {t.externalLinkText}
          <ExternalLink size={16} />
        </a>
      </section>

      {lightboxImage && (
        <Lightbox
          src={lightboxImage.src}
          alt={lightboxImage.alt}
          isOpen={!!lightboxImage}
          onClose={() => setLightboxImage(null)}
        />
      )}
    </div>
  );
}
